/**
 * 加密模块 - 基于 Web Crypto API
 * 在 Cloudflare Workers / 浏览器 / Node 18+ 均可运行
 *
 * 设计：
 * - 用户主密码 (masterPassword) 通过 PBKDF2 派生 AES-GCM 密钥
 * - 每个密码条目用独立的盐(salt) + 初始化向量(iv) 加密
 * - 服务端只存密文，主密码由客户端持有 -> 端到端加密
 */

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/** 把字符串编码为 Uint8Array */
export function toBytes(s: string): Uint8Array<ArrayBuffer> {
  return encoder.encode(s) as Uint8Array<ArrayBuffer>;
}

/** 把 Uint8Array 解码为字符串 */
export function fromBytes(b: ArrayBuffer | Uint8Array): string {
  return decoder.decode(b);
}

/** base64 编码 (Uint8Array/ArrayBuffer -> string) */
export function toBase64(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = "";
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary);
}

/** base64 解码 (string -> Uint8Array) */
export function fromBase64(b64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes as Uint8Array<ArrayBuffer>;
}

/** 生成随机字节 */
export function randomBytes(length: number): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(length) as Uint8Array<ArrayBuffer>;
  crypto.getRandomValues(bytes);
  return bytes;
}

/** 生成 UUID */
export function uuid(): string {
  return crypto.randomUUID();
}

/**
 * 用 PBKDF2 从主密码派生 AES-GCM 密钥
 * @param masterPassword 主密码
 * @param salt 盐(Uint8Array)
 * @param iterations 迭代次数（越高越安全，Workers 上建议 150k）
 */
export async function deriveKey(
  masterPassword: string,
  salt: Uint8Array,
  iterations = 150000
): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey(
    "raw",
    toBytes(masterPassword),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt as BufferSource,
      iterations,
      hash: "SHA-256",
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

/**
 * AES-GCM 加密
 * @returns { cipher: base64密文, iv: base64初始向量, salt: base64盐 }
 */
export async function encrypt(
  masterPassword: string,
  plaintext: string
): Promise<{ cipher: string; iv: string; salt: string }> {
  const salt = randomBytes(16);
  const iv = randomBytes(12);
  const key = await deriveKey(masterPassword, salt);
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv as BufferSource },
    key,
    toBytes(plaintext)
  );
  return {
    cipher: toBase64(encrypted),
    iv: toBase64(iv),
    salt: toBase64(salt),
  };
}

/**
 * AES-GCM 解密
 * @param masterPassword 主密码
 * @param cipher base64 密文
 * @param ivString base64 初始向量
 * @param saltString base64 盐
 * @returns 明文字符串
 */
export async function decrypt(
  masterPassword: string,
  cipher: string,
  ivString: string,
  saltString: string
): Promise<string> {
  const salt = fromBase64(saltString);
  const iv = fromBase64(ivString);
  const key = await deriveKey(masterPassword, salt);
  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: iv as BufferSource },
    key,
    fromBase64(cipher)
  );
  return fromBytes(decrypted);
}

/** 密码强度评分 0-4 */
export function passwordStrength(password: string): number {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;
  return Math.min(4, score);
}

/** 生成高强度随机密码 */
export function generatePassword(length = 16): string {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:,.<>?";
  const bytes = randomBytes(length);
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars[bytes[i] % chars.length];
  }
  // 确保至少包含一位数字和一位特殊字符
  if (!/\d/.test(result) || !/[^a-zA-Z0-9]/.test(result)) {
    return generatePassword(length);
  }
  return result;
}